import { Box, Center } from "@chakra-ui/react";
import { ResponsiveChoropleth } from "@nivo/geo";
import React from "react";
import { format } from 'd3-format';
import { LibBoxDNASpinner } from "../../../components/lib";
import { Status } from "../../../components/shared/Status";

import { useReadPublicChart } from "../../../services/api/charts/mutations/useReadPublicChart";
import {
  getPublicCasesMapChart,
  ICasesMapInputs,
} from "../utils/getPublicCasesMapChart";

interface IProps {
  registryAbbr: string;
}

const initialCaseOptions = {
  data: [],
  domain: [],
  features: [],
};

const CaseMapChart = ({ registryAbbr }: IProps) => {
  const [casesMapOptions, setCasesMapOptions] =
    React.useState<ICasesMapInputs>(initialCaseOptions);

  const {
    data: dataCharts,
    isLoading,
    isError,
  } = useReadPublicChart({
    registryAbbr,
    chartName: "map",
  });

  React.useEffect(() => {
    const resCaseOptionsUpdate = getPublicCasesMapChart(dataCharts);
    if (resCaseOptionsUpdate) {
      setCasesMapOptions(resCaseOptionsUpdate);
    }
  }, [dataCharts]);

  function getColorScale(roundedMax){
    const numBins = roundedMax / 10;
    const colorScale = [
      '#ffffe5',
      '#fff7bc',
      '#fee391',
      '#fec44f',
      '#fe9929',
      '#ec7014',
      '#cc4c02',
      '#993404',
      '#662506',
      '#420000'
  ];
  return colorScale.slice(0, numBins);
  }
  const mapColors = casesMapOptions.domain[1] ? getColorScale(casesMapOptions.domain[1]) : [];

  const costumeValue = (value)=> {
    if (value === 1.0){
      return '1';
    }
    else{
      const valueFormatter = ".2s"; 
      return format(valueFormatter)(value); 
    }
  }; 

  // Tooltip code 
  const createToolTip
   = ({ feature }) => {
    // Value is the number of participants in each country
    const value = feature.value;
    if (value === undefined || value === null || isNaN(value)) {
      return null;
    }

    let lowerBound, upperBound; 

    // If the value (number of participants) < 5
    if (value < 5){
      lowerBound = 1;
      upperBound = 5; 
        
    // If the value (number of participants) is more than 5 and less than 11 
    } else if (value >= 5 && value < 11){
      lowerBound = 5; 
      upperBound = 11;
    } else {
      lowerBound = Math.floor(value/10) * 10; 
      upperBound = lowerBound + 10; 
    }

    return(
      <div style={{ backgroundColor: 'white', padding: '5px', borderRadius: '10px', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' }}>
      <strong>{feature.label}</strong>
      <br />
      <strong>Range: {lowerBound} - {upperBound}</strong>
    </div>
    );
  
  };
  return (
    <Box h="full" w="full">
      {isLoading && (
        <Center h="full">
          <LibBoxDNASpinner />
        </Center>
      )}
      {isError && (
        <Center h="full">
          <Box>
            <Status.IsError></Status.IsError>
          </Box>
        </Center>
      )}
      {!isLoading && dataCharts && (
        <ResponsiveChoropleth
          data={casesMapOptions?.data}
          features={casesMapOptions?.features}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          colors="YlOrBr"
          domain={casesMapOptions?.domain}
          unknownColor="#666666"
          projectionScale={130}
          label="properties.name"
          valueFormat={costumeValue}
          projectionType="equalEarth"
          projectionTranslation={[0.5, 0.5]}
          projectionRotation={[0, 0, 0]}
          enableGraticule={true}
          graticuleLineColor="#dddddd"
          borderWidth={0.5}
          borderColor="#152538"
          legends={[
            {
              anchor: "bottom-left",
              direction: "column",
              justify: true,
              translateX: 20,
              translateY: -10,
              itemsSpacing: 0,
              itemWidth: 94,
              itemHeight: 18,
              itemDirection: "left-to-right",
              itemTextColor: "#444444",
              itemOpacity: 0.85,
              symbolSize: 18,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemTextColor: "#000000",
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        />
      )}
    </Box>
  );
};

export default CaseMapChart;
